import React, { useState, useEffect } from 'react';
import { API_URL } from '../../utils/api';
import { LoadingSpinner } from '../ui/Icons';
import confetti from 'canvas-confetti';
import { QRCodeSVG } from 'qrcode.react';

const PartyHost = ({ pin, lobbyId, fetchWithAuth, onExit }) => {
    const [gameState, setGameState] = useState(null);
    const [loading, setLoading] = useState(true);
    const [showShareModal, setShowShareModal] = useState(false);

    // Polling
    useEffect(() => {
        const interval = setInterval(loadStatus, 2000); // 2s polling
        loadStatus();
        return () => clearInterval(interval);
    }, [pin]);

    const loadStatus = async () => {
        try {
            const res = await fetch(`${API_URL}/api/party/status/?pin=${pin}`);
            if (res.ok) {
                const data = await res.json();
                setGameState(data);
                if (data.status === 'FINISHED') fireConfetti();
            }
        } catch (e) {
            console.error(e);
        } finally {
            setLoading(false);
        }
    };

    const sendAction = async (action) => {
        try {
            await fetchWithAuth(`${API_URL}/api/party/control/`, {
                method: 'POST',
                body: JSON.stringify({ pin, action })
            });
            loadStatus(); // Instant update
        } catch (e) {
            console.error(e);
        }
    };

    const fireConfetti = () => {
        confetti({
            particleCount: 150,
            spread: 70,
            origin: { y: 0.6 }
        });
    };

    const getJoinUrl = () => {
        // Generate join URL with pre-filled PIN
        const baseUrl = window.location.origin;
        return `${baseUrl}/party?pin=${pin}`;
    };

    const shareViaWhatsApp = () => {
        const message = `🌿 Komm ins Plantlix Party!\n\nPIN: ${pin}\nOder klick hier: ${getJoinUrl()}`;
        const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(message)}`;
        window.open(whatsappUrl, '_blank');
    };

    const shareViaSniggle = () => {
        // Navigate to Sniggle with pre-filled message
        const message = `🌿 Komm ins Plantlix Party! PIN: ${pin}`;
        window.location.href = `/sniggle?share=${encodeURIComponent(message)}`;
    };

    if (loading && !gameState) return <div className="h-screen flex items-center justify-center"><LoadingSpinner /></div>;

    const players = gameState?.players_list || [];
    const sortedPlayers = [...players].sort((a, b) => b.score - a.score);

    return (
        <div className="min-h-screen bg-slate-900 text-white p-6 flex flex-col">
            {/* Header */}
            <div className="flex justify-between items-center mb-8 bg-slate-800 p-4 rounded-xl shadow-lg border border-slate-700">
                <div>
                    <span className="text-slate-400 uppercase text-xs font-bold tracking-wider block">Game PIN</span>
                    <span className="text-5xl font-mono font-bold tracking-widest text-plantlix-400">{pin}</span>
                </div>
                <div className="text-right">
                    <span className="text-slate-400 text-sm">Spieler</span>
                    <div className="text-3xl font-bold">{gameState?.players_count}</div>
                </div>
            </div>

            {/* WAITING ROOM */}
            {gameState?.status === 'WAITING' && (
                <div className="flex-1 flex flex-col items-center justify-center">
                    <h2 className="text-4xl font-bold mb-8 animate-pulse">Warten auf Spieler...</h2>

                    {/* Share Tools */}
                    <div className="mb-8 flex gap-4">
                        <button
                            onClick={() => setShowShareModal(true)}
                            className="bg-blue-600 hover:bg-blue-700 px-6 py-3 rounded-lg font-bold text-lg shadow-lg transition-transform hover:scale-105 flex items-center gap-2"
                        >
                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
                            </svg>
                            Einladen
                        </button>
                        <button
                            onClick={shareViaWhatsApp}
                            className="bg-plantlix-600 hover:bg-plantlix-700 px-6 py-3 rounded-lg font-bold text-lg shadow-lg transition-transform hover:scale-105 flex items-center gap-2"
                        >
                            <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
                            </svg>
                            WhatsApp
                        </button>
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 w-full max-w-4xl mb-12">
                        {players.map((p, i) => (
                            <div key={i} className="bg-slate-800 p-4 rounded-lg text-center font-bold text-xl border border-slate-700 animate-in fade-in zoom-in duration-300">
                                {p.nickname}
                            </div>
                        ))}
                    </div>
                    {players.length > 0 && (
                        <button
                            onClick={() => sendAction('start')}
                            className="bg-plantlix-500 hover:bg-plantlix-600 text-white px-12 py-4 rounded-full font-bold text-2xl shadow-xl transition-transform hover:scale-105"
                        >
                            Spiel starten
                        </button>
                    )}
                </div>
            )}

            {/* Share Modal */}
            {showShareModal && (
                <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4" onClick={() => setShowShareModal(false)}>
                    <div className="bg-slate-800 rounded-2xl p-8 max-w-md w-full shadow-2xl border border-slate-700" onClick={(e) => e.stopPropagation()}>
                        <div className="flex justify-between items-center mb-6">
                            <h3 className="text-2xl font-bold">Freunde einladen</h3>
                            <button
                                onClick={() => setShowShareModal(false)}
                                className="text-slate-400 hover:text-white transition-colors"
                            >
                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>

                        {/* QR Code */}
                        <div className="bg-white p-6 rounded-xl mb-6 flex justify-center">
                            <QRCodeSVG value={getJoinUrl()} size={200} level="H" />
                        </div>

                        {/* PIN Display */}
                        <div className="text-center mb-6">
                            <p className="text-slate-400 text-sm mb-2">Game PIN</p>
                            <p className="text-5xl font-mono font-bold text-plantlix-400 tracking-wider">{pin}</p>
                        </div>

                        {/* Share Buttons */}
                        <div className="space-y-3">
                            <button
                                onClick={shareViaWhatsApp}
                                className="w-full bg-plantlix-600 hover:bg-plantlix-700 px-6 py-4 rounded-lg font-bold text-lg shadow-lg transition-all hover:scale-105 flex items-center justify-center gap-3"
                            >
                                <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
                                </svg>
                                Via WhatsApp teilen
                            </button>
                            <button
                                onClick={shareViaSniggle}
                                className="w-full bg-purple-600 hover:bg-purple-700 px-6 py-4 rounded-lg font-bold text-lg shadow-lg transition-all hover:scale-105 flex items-center justify-center gap-3"
                            >
                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
                                </svg>
                                Via Sniggle teilen
                            </button>
                            <button
                                onClick={() => {
                                    navigator.clipboard.writeText(getJoinUrl());
                                    alert('Link kopiert!');
                                }}
                                className="w-full bg-slate-700 hover:bg-slate-600 px-6 py-4 rounded-lg font-bold text-lg shadow-lg transition-all hover:scale-105 flex items-center justify-center gap-3"
                            >
                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                </svg>
                                Link kopieren
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* QUESTION PHASE */}
            {(gameState?.status === 'QUESTION' || gameState?.status === 'REVEAL') && (
                <div className="flex-1 flex flex-col">
                    <div className="flex justify-between items-end mb-6">
                        <div className="text-xl text-slate-400">Runde {gameState.round}</div>
                        {gameState.status === 'QUESTION' && (
                            <button onClick={() => sendAction('reveal')} className="bg-yellow-500 hover:bg-yellow-600 px-6 py-2 rounded-lg font-bold">
                                Auflösen
                            </button>
                        )}
                        {gameState.status === 'REVEAL' && (
                            <button onClick={() => sendAction('next_round')} className="bg-blue-500 hover:bg-blue-600 px-6 py-2 rounded-lg font-bold">
                                Nächste Runde →
                            </button>
                        )}
                    </div>

                    {/* Question Image - The Plant to Identify */}
                    {gameState.question_image && (
                        <div className="bg-white p-4 rounded-2xl shadow-2xl mb-6">
                            <img
                                src={gameState.question_image.startsWith('http') ? gameState.question_image : `https://staging.plantlix.de${gameState.question_image}`}
                                alt="Pflanze identifizieren"
                                className="w-full max-h-[400px] object-contain rounded-xl"
                            />
                        </div>
                    )}

                    <div className="bg-white text-slate-900 p-8 rounded-2xl shadow-2xl text-center mb-8 min-h-[100px] flex items-center justify-center">
                        <h2 className="text-5xl font-extrabold leading-tight">{gameState.question}</h2>
                    </div>

                    <div className="grid grid-cols-2 gap-6 flex-1 max-h-[500px]">
                        {gameState.options?.map((opt) => {
                            const isCorrect = gameState.status === 'REVEAL' && opt.id === gameState.correct_answer;
                            const isDimmed = gameState.status === 'REVEAL' && opt.id !== gameState.correct_answer;

                            return (
                                <div
                                    key={opt.id}
                                    className={`
                                        relative rounded-xl p-6 flex flex-col items-center justify-center text-center transition-all duration-500
                                        ${opt.id === 'A' ? 'bg-red-500' : ''}
                                        ${opt.id === 'B' ? 'bg-blue-500' : ''}
                                        ${opt.id === 'C' ? 'bg-yellow-500' : ''}
                                        ${opt.id === 'D' ? 'bg-plantlix-500' : ''}
                                        ${isDimmed ? 'opacity-30 scale-95' : 'opacity-100'}
                                        ${isCorrect ? 'ring-8 ring-white scale-105 z-10 shadow-2xl' : ''}
                                    `}
                                >
                                    {(opt.image_medium || opt.image) && <img src={opt.image_medium || opt.image} alt="Plant" loading="lazy" className="w-32 h-32 object-cover rounded-lg mb-4 shadow-md bg-white" />}
                                    <div className="text-2xl font-bold text-white drop-shadow-md">{opt.text}</div>
                                    <div className="absolute top-4 left-4 bg-black/30 w-10 h-10 rounded-full flex items-center justify-center font-bold text-white">
                                        {opt.id}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            {/* LEADERBOARD / FINISHED */}
            {(gameState?.status === 'FINISHED') && (
                <div className="flex-1 flex flex-col items-center justify-center overflow-y-auto p-6">
                    <h2 className="text-5xl font-black text-transparent bg-clip-text bg-gradient-to-r from-yellow-300 via-yellow-500 to-orange-500 mb-4">
                        🏆 Spiel beendet! 🏆
                    </h2>

                    {gameState.final_stats && (
                        <>
                            {/* Podium - Top 3 */}
                            {gameState.final_stats.rankings?.length > 0 && (
                                <div className="w-full max-w-4xl mb-8">
                                    <div className="flex items-end justify-center gap-4 mb-8 min-h-[300px]">
                                        {/* 2nd Place */}
                                        {gameState.final_stats.rankings[1] && (
                                            <div className="flex flex-col items-center animate-in slide-in-from-bottom duration-500 delay-100">
                                                <div className="text-6xl mb-2">🥈</div>
                                                <div className="bg-slate-700 rounded-t-2xl p-6 w-48 h-44 flex flex-col items-center justify-end border-4 border-slate-600">
                                                    <div className="font-bold text-2xl mb-2">{gameState.final_stats.rankings[1].nickname}</div>
                                                    <div className="text-plantlix-400 text-3xl font-black">{gameState.final_stats.rankings[1].score}</div>
                                                    <div className="text-slate-400 text-sm">🔥 {gameState.final_stats.rankings[1].streak}</div>
                                                </div>
                                                <div className="bg-slate-600 w-full h-8 rounded-b-lg text-center font-bold text-xl">#2</div>
                                            </div>
                                        )}

                                        {/* 1st Place (Winner) */}
                                        {gameState.final_stats.rankings[0] && (
                                            <div className="flex flex-col items-center animate-in slide-in-from-bottom duration-500">
                                                <div className="text-8xl mb-2">👑</div>
                                                <div className="bg-gradient-to-br from-yellow-400 to-yellow-600 rounded-t-2xl p-6 w-56 h-56 flex flex-col items-center justify-end border-4 border-yellow-500 shadow-2xl">
                                                    <div className="font-black text-3xl mb-3 text-slate-900">{gameState.final_stats.rankings[0].nickname}</div>
                                                    <div className="text-slate-900 text-4xl font-black">{gameState.final_stats.rankings[0].score}</div>
                                                    <div className="text-slate-800 text-sm font-bold">🔥 {gameState.final_stats.rankings[0].streak}</div>
                                                </div>
                                                <div className="bg-yellow-500 w-full h-12 rounded-b-lg text-center font-black text-2xl text-slate-900">#1</div>
                                            </div>
                                        )}

                                        {/* 3rd Place */}
                                        {gameState.final_stats.rankings[2] && (
                                            <div className="flex flex-col items-center animate-in slide-in-from-bottom duration-500 delay-200">
                                                <div className="text-6xl mb-2">🥉</div>
                                                <div className="bg-slate-700 rounded-t-2xl p-6 w-48 h-36 flex flex-col items-center justify-end border-4 border-slate-600">
                                                    <div className="font-bold text-xl mb-2">{gameState.final_stats.rankings[2].nickname}</div>
                                                    <div className="text-plantlix-400 text-2xl font-black">{gameState.final_stats.rankings[2].score}</div>
                                                    <div className="text-slate-400 text-sm">🔥 {gameState.final_stats.rankings[2].streak}</div>
                                                </div>
                                                <div className="bg-slate-600 w-full h-6 rounded-b-lg text-center font-bold text-lg">#3</div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}

                            {/* Full Rankings Table */}
                            <div className="w-full max-w-3xl bg-slate-800 rounded-2xl p-6 border border-slate-700 shadow-2xl mb-6">
                                <h3 className="text-2xl font-bold mb-4 text-center">Alle Platzierungen</h3>
                                <div className="space-y-2">
                                    {gameState.final_stats.rankings?.map((player, idx) => (
                                        <div
                                            key={idx}
                                            className={`flex justify-between items-center p-4 rounded-lg transition-all ${idx === 0 ? 'bg-yellow-500/20 border-2 border-yellow-500/50' :
                                                    idx === 1 ? 'bg-slate-600/40 border-2 border-slate-500/30' :
                                                        idx === 2 ? 'bg-orange-500/20 border-2 border-orange-500/30' :
                                                            'bg-slate-700'
                                                }`}
                                        >
                                            <div className="flex items-center gap-4">
                                                <div className={`font-mono font-black text-2xl ${idx < 3 ? 'text-yellow-400' : 'text-slate-400'}`}>
                                                    #{player.rank}
                                                </div>
                                                <div className="font-bold text-xl">{player.nickname}</div>
                                                {idx === 0 && <span className="text-2xl">👑</span>}
                                            </div>
                                            <div className="flex items-center gap-6">
                                                <div className="text-slate-400 text-sm">
                                                    🔥 <span className="font-bold">{player.streak}</span>
                                                </div>
                                                <div className="font-black text-2xl text-plantlix-400">{player.score}</div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* Game Stats Summary */}
                            <div className="w-full max-w-3xl bg-slate-800/50 rounded-xl p-4 border border-slate-700 mb-6">
                                <div className="grid grid-cols-3 gap-4 text-center">
                                    <div>
                                        <div className="text-slate-400 text-sm">Runden</div>
                                        <div className="text-2xl font-bold">{gameState.final_stats.total_rounds}</div>
                                    </div>
                                    <div>
                                        <div className="text-slate-400 text-sm">Spieler</div>
                                        <div className="text-2xl font-bold">{gameState.final_stats.total_players}</div>
                                    </div>
                                    <div>
                                        <div className="text-slate-400 text-sm">Dauer</div>
                                        <div className="text-2xl font-bold">{Math.round(gameState.final_stats.game_duration / 60)}m</div>
                                    </div>
                                </div>
                            </div>

                            {/* Action Buttons */}
                            <div className="flex gap-4">
                                <button
                                    onClick={() => window.location.reload()}
                                    className="bg-plantlix-600 hover:bg-plantlix-700 px-8 py-4 rounded-full font-bold text-xl shadow-xl transition-all hover:scale-105 flex items-center gap-3"
                                >
                                    🔄 Nochmal spielen
                                </button>
                                <button
                                    onClick={onExit}
                                    className="bg-slate-700 hover:bg-slate-600 px-8 py-4 rounded-full font-bold text-xl shadow-xl transition-all hover:scale-105"
                                >
                                    Zurück zum Menü
                                </button>
                            </div>
                        </>
                    )}
                </div>
            )}
        </div>
    );
};

export default PartyHost;
